import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, CheckCircle, Heart, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Marquee from "react-fast-marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const relationshipOptions = [
  "Teacher/Educator", "School Counselor", "Social Worker", "Family Member",
  "Community Leader", "Coach/Mentor", "Healthcare Provider", "Faith Leader", "Other Professional"
];

const ReferralForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    referrer_name: "", referrer_email: "", referrer_phone: "", referrer_relationship: "",
    youth_name: "", youth_age: "", youth_phone: "", youth_email: "",
    parent_guardian_name: "", parent_guardian_phone: "",
    reason_for_referral: "", specific_needs: "", additional_info: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = { ...formData, youth_age: parseInt(formData.youth_age) };
      await axios.post(`${API}/referral`, submitData);
      setSubmitted(true);
      toast.success("Referral submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit referral. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20 bg-white" data-testid="referral-success">
        <div className="card-elevated max-w-lg mx-auto rounded-3xl p-10 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-soar-teal/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-soar-teal" />
          </div>
          <h2 className="font-heading text-3xl text-soar-blue mb-4 tracking-tight">Referral Submitted!</h2>
          <p className="text-soar-blue/60 mb-6">Thank you for referring a youth to S.O.A.R Solutions. We will review the referral and reach out to connect with the youth and their family.</p>
          <p className="font-accent text-3xl text-soar-gold">Together we lift others higher!</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="referral-form-page" className="bg-white">

      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/dgk9gmp9_Kaiden_Braneil.png" alt="Youth mentorship" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/95 to-soar-blue/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl space-y-6">
            <span className="inline-block border border-soar-gold/40 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-gold uppercase opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
              Refer a Youth
            </span>
            <h1 className="font-heading text-6xl sm:text-7xl leading-none tracking-tight text-white opacity-0 animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
              Know a Youth Who <span className="text-soar-gold">Needs Support?</span>
            </h1>
            <p className="text-xl text-white/80 opacity-0 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
              If you know a young person aged 13-25 who could benefit from mentorship, life skills training,
              or community support, we'd love to hear from you. Your referral could change a life.
            </p>
            <div className="card-elevated rounded-2xl p-6 opacity-0 animate-fade-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
              <p className="font-heading text-soar-gold text-sm mb-2">Our Promise:</p>
              <p className="text-soar-blue/60 text-sm">"If we haven't figured out how to make it easier for you to grow as a person, then we're probably not finished yet."</p>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-5" style={{ backgroundColor: '#212163' }}>
        <Marquee speed={50} gradient={false}>
          {["REFER", "CONNECT", "SUPPORT", "EMPOWER", "TRANSFORM", "UPLIFT"].map((w) => (
            <span key={w} className="font-heading text-4xl mx-12 tracking-wider" style={{ color: 'rgba(221,182,77,0.3)' }}>{w}</span>
          ))}
        </Marquee>
      </div>

      {/* Referral Form */}
      <section className="py-20" data-testid="referral-form-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="card-elevated rounded-3xl p-8 sm:p-10">
            <h2 className="font-heading text-3xl text-soar-blue mb-2 tracking-tight">Youth Referral Form</h2>
            <p className="text-soar-blue/50 mb-8">Fill out the form below to refer a youth to our programs. All fields marked with * are required.</p>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Referrer Information */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Your Information (Referrer)</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="referrer_name" className="text-soar-blue/70">Your Name *</Label>
                    <Input id="referrer_name" name="referrer_name" value={formData.referrer_name} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="referrer-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="referrer_email" className="text-soar-blue/70">Your Email *</Label>
                    <Input id="referrer_email" name="referrer_email" type="email" value={formData.referrer_email} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="referrer-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="referrer_phone" className="text-soar-blue/70">Your Phone *</Label>
                    <Input id="referrer_phone" name="referrer_phone" type="tel" value={formData.referrer_phone} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="referrer-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="referrer_relationship" className="text-soar-blue/70">Your Relationship to Youth *</Label>
                    <Select value={formData.referrer_relationship} onValueChange={(value) => handleSelectChange("referrer_relationship", value)}>
                      <SelectTrigger className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="referrer-relationship">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-soar-blue/10">
                        {relationshipOptions.map(option => (<SelectItem key={option} value={option}>{option}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Youth Information */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Youth Information</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="youth_name" className="text-soar-blue/70">Youth's Name *</Label>
                    <Input id="youth_name" name="youth_name" value={formData.youth_name} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="youth-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="youth_age" className="text-soar-blue/70">Youth's Age *</Label>
                    <Input id="youth_age" name="youth_age" type="number" min="13" max="25" value={formData.youth_age} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="youth-age" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="youth_phone" className="text-soar-blue/70">Youth's Phone (if available)</Label>
                    <Input id="youth_phone" name="youth_phone" type="tel" value={formData.youth_phone} onChange={handleChange} className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="youth-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="youth_email" className="text-soar-blue/70">Youth's Email (if available)</Label>
                    <Input id="youth_email" name="youth_email" type="email" value={formData.youth_email} onChange={handleChange} className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="youth-email" />
                  </div>
                </div>
              </div>

              {/* Parent/Guardian Information */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Parent/Guardian Information (for minors)</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="parent_guardian_name" className="text-soar-blue/70">Parent/Guardian Name</Label>
                    <Input id="parent_guardian_name" name="parent_guardian_name" value={formData.parent_guardian_name} onChange={handleChange} className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="parent-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="parent_guardian_phone" className="text-soar-blue/70">Parent/Guardian Phone</Label>
                    <Input id="parent_guardian_phone" name="parent_guardian_phone" type="tel" value={formData.parent_guardian_phone} onChange={handleChange} className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue" data-testid="parent-phone" />
                  </div>
                </div>
              </div>

              {/* Referral Details */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Referral Details</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reason_for_referral" className="text-soar-blue/70">Reason for Referral *</Label>
                    <Textarea id="reason_for_referral" name="reason_for_referral" value={formData.reason_for_referral} onChange={handleChange} required className="rounded-xl min-h-[120px] bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-soar-blue/30" placeholder="Why are you referring this youth? What support do you think they need?" data-testid="referral-reason" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="specific_needs" className="text-soar-blue/70">Specific Needs or Challenges</Label>
                    <Textarea id="specific_needs" name="specific_needs" value={formData.specific_needs} onChange={handleChange} className="rounded-xl min-h-[100px] bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-soar-blue/30" placeholder="Are there any specific challenges or needs we should be aware of?" data-testid="specific-needs" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="additional_info" className="text-soar-blue/70">Additional Information</Label>
                    <Textarea id="additional_info" name="additional_info" value={formData.additional_info} onChange={handleChange} className="rounded-xl min-h-[80px] bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-soar-blue/30" placeholder="Any other information that would help us serve this youth better..." data-testid="additional-info" />
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={submitting} className="w-full rounded-full bg-soar-teal hover:bg-soar-teal/90 py-6 text-lg font-bold hover:scale-[1.02] transition-transform duration-200" data-testid="referral-submit-btn">
                {submitting ? "Submitting..." : (<>Submit Referral <ArrowRight className="w-5 h-5 ml-2" /></>)}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReferralForm;
